// =============================================================================
// CATALOGUE - Gestion des produits, services et packs
// =============================================================================
// Inclut : sélection multiple + actions groupées + filtres avancés
// =============================================================================
import { useState, useEffect, useCallback } from "react";
// 🔴🔴 N°728-4 — « Fiche CEE » est le vocabulaire du RÉGIME D'AIDES CEE.
// ⚠️ MESURÉ en PROD (mario_bat) : la COLONNE « Fiche CEE » du tableau Produits
// ⛔ n'avait AUCUNE condition, tandis que les deux blocs du FORMULAIRE étaient
// gatés… par un SLUG EN DUR (`tenantId === "climatpac" || "omv_france"`), la
// faute même que les N°690/N°705 ont supprimée ailleurs.
// 🔑 Une seule autorité : le RÉGIME D'AIDES déclaré (`regime_aides` contient
// « cee »). ⛔ Aucun slug, et ⛔ aucune règle en creux.
import { isInstallateurProfile, hasAmoMissions, getRegimeAides, hasIntegrationPennylane } from "../../utils/tenantProfile";
import BaseTarificationField from "./BaseTarificationField";
// 🔑 N°889 — source UNIQUE du formatage d'un 422 (3 sites la partageaient en
// double dans cet écran, et d'autres écrans portent la même faute).
import { messageErreurValidation, champsObligatoiresManquants, refSaisieOuOmise } from "../../utils/erreursValidation";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { 
  Package, 
  Wrench, 
  Boxes, 
  Plus, 
  Search, 
  Edit, 
  Trash2,
  Loader2,
  ToggleLeft,
  ToggleRight,
  Euro,
  RefreshCw,
  Download,
  Upload,
  FileSpreadsheet
} from "lucide-react";
import SmartLegalEditor from "../../components/editors/SmartLegalEditor";
import MissionsAMOTab from "./MissionsAMOTab";
import { toast } from "sonner";
import { useConfigList } from "../../hooks/useConfigList";
import { enteteTenant } from "../../utils/enteteTenant";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper pour construire les headers avec authentification
const getAuthHeaders = (tenantId) => {
  const token = localStorage.getItem('session_token');
  return {
    ...enteteTenant(tenantId),
    "Content-Type": "application/json",
    ...(token && { "Authorization": `Bearer ${token}` })
  };
};

// ⛔ Absence de régime déclaré ⇒ PAS de vocabulaire CEE (défaut sûr).
const aRegimeCEE = () => getRegimeAides().includes("cee");


export default function CataloguePage({ tenantId = "jkc" }) {
  const isOmv = isInstallateurProfile();  // profil installateur (OMV/Climat) : masque Missions AMO (concept AMO/TEF)
  // 🔴 L'onglet Missions AMO dépend d'une CAPABILITÉ DÉCLARÉE,
  // ⛔ plus d'une exclusion. Absente ⇒ on ⛔ n'affiche PAS.
  const peutVoirMissionsAmo = hasAmoMissions();
  const { items: catProduitItems } = useConfigList("categories_produit");
  const { items: catServiceItems } = useConfigList("categories_service");
  const { items: typesProduitItems } = useConfigList("types_produit");
  const { items: typesServiceItems } = useConfigList("types_service");
  const [activeTab, setActiveTab] = useState("produits");
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [produits, setProduits] = useState([]);
  const [services, setServices] = useState([]);
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategorie, setFilterCategorie] = useState("all");
  
  // Filtres par colonne PRODUITS
  const [filterNom, setFilterNom] = useState("");
  const [filterFiche, setFilterFiche] = useState("");
  const [filterMarque, setFilterMarque] = useState("");
  const [filterCategorieCol, setFilterCategorieCol] = useState("all");
  // 🔑 Décision CTO 01/10 : ⛔ filtre « Actif » par défaut GLOBAL (tous tenants). Statut =
  //    « Tous » par défaut ; les ARCHIVÉS, eux, ne sont jamais renvoyés par l'API des listes.
  const [filterStatutCol, setFilterStatutCol] = useState("all");
  
  // Filtres par colonne SERVICES (séparés des produits)
  const [filterServiceNom, setFilterServiceNom] = useState("");
  const [filterServiceCategorieCol, setFilterServiceCategorieCol] = useState("all");
  const [filterServiceStatutCol, setFilterServiceStatutCol] = useState("all");
  
  // Sélection multiple - PRODUITS
  const [selectedProduits, setSelectedProduits] = useState(new Set());
  const [selectAllProduits, setSelectAllProduits] = useState(false);
  
  // Sélection multiple - SERVICES
  const [selectedServices, setSelectedServices] = useState(new Set());
  const [selectAllServices, setSelectAllServices] = useState(false);
  
  // Dialog states
  const [showProduitDialog, setShowProduitDialog] = useState(false);
  const [showServiceDialog, setShowServiceDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [fichesCEE, setFichesCEE] = useState([]);

  // Charger les données
  useEffect(() => {
    loadCatalogue();
    loadFichesCEE();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  // Reset des sélections quand le catalogue change
  const resetSelections = useCallback(() => {
    setSelectedProduits(new Set());
    setSelectAllProduits(false);
    setSelectedServices(new Set());
    setSelectAllServices(false);
  }, []);

  const loadFichesCEE = async () => {
    try {
      const headers = getAuthHeaders(tenantId);
      const res = await fetch(`${API_URL}/api/metier/aides/fiches-cee`, { headers, credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        // Transformer en format pour le Select
        const options = data.map(f => ({
          value: f.code,
          label: `${f.code} - ${f.nom}`
        }));
        setFichesCEE(options);
      }
    } catch (error) {
      console.error("Erreur chargement fiches CEE:", error);
    }
  };

  const loadCatalogue = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders(tenantId);
      
      // actif_only=false : charger AUSSI les produits/services désactivés pour qu'ils
      // restent visibles dans la grille (badge "Inactif" + filtre Statut, défaut "Tous").
      // Sans ça, un produit désactivé disparaissait (= "fantôme").
      const [produitsRes, servicesRes, packsRes] = await Promise.all([
        fetch(`${API_URL}/api/metier/catalogue/produits?actif_only=false`, { headers, credentials: 'include' }),
        fetch(`${API_URL}/api/metier/catalogue/services?actif_only=false`, { headers, credentials: 'include' }),
        fetch(`${API_URL}/api/metier/catalogue/packs`, { headers, credentials: 'include' })
      ]);

      if (produitsRes.ok) {
        const produitsData = await produitsRes.json();
        console.log("Produits chargés:", produitsData.length, "items");
        setProduits(produitsData);
      } else {
        console.error("Erreur chargement produits:", produitsRes.status);
      }
      if (servicesRes.ok) setServices(await servicesRes.json());
      if (packsRes.ok) setPacks(await packsRes.json());
      resetSelections();
    } catch (error) {
      console.error("Erreur chargement catalogue:", error);
      toast.error("Erreur lors du chargement du catalogue");
    } finally {
      setLoading(false);
    }
  };

  // Export CSV
  const handleExportCSV = () => {
    const items = activeTab === "produits" ? produits : services;
    const type = activeTab === "produits" ? "produits" : "services";
    
    if (items.length === 0) {
      toast.warning("Aucun élément à exporter");
      return;
    }

    // Déterminer les colonnes
    const columns = activeTab === "produits" 
      ? ["reference", "nom", "categorie", "marque", "modele", "prix_ht", "taux_tva", "fiche_cee", "actif", "description"]
      : ["reference", "nom", "categorie", "prix_ht", "taux_tva", "unite", "actif", "description"];

    // En-tête CSV
    let csv = columns.join(";") + "\n";

    // Données
    items.forEach(item => {
      const row = columns.map(col => {
        let val = item[col] ?? "";
        if (typeof val === "boolean") val = val ? "oui" : "non";
        if (typeof val === "string" && (val.includes(";") || val.includes("\n"))) {
          val = `"${val.replace(/"/g, '""')}"`;
        }
        return val;
      });
      csv += row.join(";") + "\n";
    });

    // Télécharger
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `catalogue_${type}_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    
    toast.success(`${items.length} ${type} exportés`);
  };

  // Import CSV
  const handleImportCSV = async () => {
    if (!importFile) return;

    setImporting(true);
    try {
      const text = await importFile.text();
      const lines = text.split("\n").filter(l => l.trim());
      
      if (lines.length < 2) {
        toast.error("Fichier CSV vide ou invalide");
        return;
      }

      // Parser l'en-tête
      const headers = lines[0].split(";").map(h => h.trim().toLowerCase().replace(/"/g, ""));
      
      // Parser les lignes
      const items = [];
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(";").map(v => v.trim().replace(/^"|"$/g, ""));
        const item = {};
        headers.forEach((h, idx) => {
          let val = values[idx] || "";
          if (h === "actif") {
            // Colonne actif vide/absente → actif par défaut (true). Seules les
            // valeurs explicitement négatives (non/false/0) désactivent.
            const t = val.trim().toLowerCase();
            val = t === "" || t === "oui" || t === "true" || t === "1";
          }
          if (h === "prix_ht" || h === "taux_tva") val = parseFloat(val) || 0;
          item[h] = val;
        });
        if (item.nom) items.push(item);
      }

      // Envoyer au backend
      const endpoint = activeTab === "produits" 
        ? `${API_URL}/api/metier/catalogue/produits/import`
        : `${API_URL}/api/metier/catalogue/services/import`;

      const res = await fetch(endpoint, {
        method: "POST",
        headers: getAuthHeaders(tenantId),
        credentials: 'include',
        body: JSON.stringify({ items })
      });

      if (res.ok) {
        const result = await res.json();
        toast.success(`${result.imported || items.length} éléments importés`);
        loadCatalogue();
        setShowImportDialog(false);
        setImportFile(null);
      } else {
        const err = await res.json();
        toast.error(messageErreurValidation(err.detail, "Erreur lors de l'import"));
      }
    } catch (error) {
      console.error("Erreur import:", error);
      toast.error("Erreur lors de l'import du fichier");
    } finally {
      setImporting(false);
    }
  };

  // Catégories de PRODUITS (équipements uniquement)
  const CATEGORIES_PRODUIT = catProduitItems.map(c => ({ value: c.id, label: c.label }));
  const CATEGORIES_SERVICE = catServiceItems.map(c => ({ value: c.id, label: c.label }));
  const TYPES_PRODUIT = typesProduitItems.map(c => ({ value: c.id, label: c.label }));
  // 🔑 Type / Catégorie PRODUIT : affichés SEULEMENT si le tenant a CONFIGURÉ la liste
  // (metier_config_listes). Liste absente ⇒ ⛔ sélecteur vide ni vocabulaire d'un autre
  // métier. Les champs restent au modèle et sont conservés à l'enregistrement.
  const avecTypeProduit = TYPES_PRODUIT.length > 0;
  const avecCategorieProduit = CATEGORIES_PRODUIT.length > 0;
  const TYPES_SERVICE = typesServiceItems.map(c => ({ value: c.id, label: c.label }));

  // Fiches CEE courantes (pour autocomplétion)
  // Note: fichesCEE est chargé depuis l'API /api/metier/aides/fiches-cee

  // Extraire les catégories uniques (utiliser les valeurs du backend)
  const categories = CATEGORIES_PRODUIT;

  // Filtrer les éléments
  const filteredProduits = produits.filter(p => {
    const matchSearch = searchTerm === "" || 
      p.designation?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.reference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.marque?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchCategorie = filterCategorie === "all" || p.categorie === filterCategorie;
    // Filtres par colonne
    const matchNom = filterNom === "" || 
      p.designation?.toLowerCase().includes(filterNom.toLowerCase()) ||
      p.reference?.toLowerCase().includes(filterNom.toLowerCase());
    const matchFiche = filterFiche === "" || p.fiche_cee?.toLowerCase().includes(filterFiche.toLowerCase());
    const matchMarque = filterMarque === "" || p.marque?.toLowerCase().includes(filterMarque.toLowerCase());
    const matchCategorieCol = filterCategorieCol === "all" || p.categorie === filterCategorieCol;
    const matchStatutCol = filterStatutCol === "all" || 
      (filterStatutCol === "actif" && p.actif) || 
      (filterStatutCol === "inactif" && !p.actif);
    return matchSearch && matchCategorie && matchNom && matchFiche && matchMarque && matchCategorieCol && matchStatutCol;
  });

  const filteredServices = services.filter(s => {
    const matchSearch = searchTerm === "" ||
      s.designation?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.reference?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchCategorie = filterCategorie === "all" || s.categorie === filterCategorie;
    // Filtres par colonne SERVICES (séparés des produits)
    const matchNom = filterServiceNom === "" || 
      s.designation?.toLowerCase().includes(filterServiceNom.toLowerCase()) ||
      s.reference?.toLowerCase().includes(filterServiceNom.toLowerCase());
    const matchCategorieCol = filterServiceCategorieCol === "all" || s.categorie === filterServiceCategorieCol;
    const matchStatutCol = filterServiceStatutCol === "all" || 
      (filterServiceStatutCol === "actif" && s.actif) || 
      (filterServiceStatutCol === "inactif" && !s.actif);
    return matchSearch && matchCategorie && matchNom && matchCategorieCol && matchStatutCol;
  });

  const filteredPacks = packs.filter(p => 
    p.nom?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Formatter le prix
  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', { 
      style: 'currency', 
      currency: 'EUR' 
    }).format(price || 0);
  };

  // === GESTION SÉLECTION PRODUITS ===
  const toggleSelectProduit = (id) => {
    const newSelected = new Set(selectedProduits);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedProduits(newSelected);
    setSelectAllProduits(newSelected.size === filteredProduits.length && filteredProduits.length > 0);
  };

  const toggleSelectAllProduits = () => {
    if (selectAllProduits) {
      setSelectedProduits(new Set());
      setSelectAllProduits(false);
    } else {
      setSelectedProduits(new Set(filteredProduits.map(p => p.id)));
      setSelectAllProduits(true);
    }
  };

  // === GESTION SÉLECTION SERVICES ===
  const toggleSelectService = (id) => {
    const newSelected = new Set(selectedServices);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedServices(newSelected);
    setSelectAllServices(newSelected.size === filteredServices.length && filteredServices.length > 0);
  };

  const toggleSelectAllServices = () => {
    if (selectAllServices) {
      setSelectedServices(new Set());
      setSelectAllServices(false);
    } else {
      setSelectedServices(new Set(filteredServices.map(s => s.id)));
      setSelectAllServices(true);
    }
  };

  // === ACTIONS GROUPÉES PRODUITS ===
  const handleBulkDeleteProduits = async () => {
    if (selectedProduits.size === 0) {
      toast.error("Aucun produit sélectionné");
      return;
    }

    if (!window.confirm(`Supprimer ${selectedProduits.size} produit(s) ?`)) return;

    let deleted = 0;
    let errors = 0;

    for (const id of selectedProduits) {
      try {
        const res = await fetch(`${API_URL}/api/metier/catalogue/produits/${id}`, {
          method: "DELETE",
          headers: getAuthHeaders(tenantId),
          credentials: 'include'
        });
        if (res.ok) deleted++;
        else errors++;
      } catch {
        errors++;
      }
    }

    if (deleted > 0) {
      toast.success(`${deleted} produit(s) supprimé(s)`);
      loadCatalogue();
    }
    if (errors > 0) {
      toast.error(`${errors} erreur(s) lors de la suppression`);
    }
  };

  const handleBulkToggleProduits = async (actif) => {
    if (selectedProduits.size === 0) {
      toast.error("Aucun produit sélectionné");
      return;
    }

    let updated = 0;
    let errors = 0;

    for (const id of selectedProduits) {
      const produit = produits.find(p => p.id === id);
      if (!produit) continue;
      
      try {
        const res = await fetch(`${API_URL}/api/metier/catalogue/produits/${id}`, {
          method: "PUT",
          headers: getAuthHeaders(tenantId),
          credentials: 'include',
          body: JSON.stringify({ ...produit, actif })
        });
        if (res.ok) updated++;
        else errors++;
      } catch {
        errors++;
      }
    }

    if (updated > 0) {
      toast.success(`${updated} produit(s) ${actif ? 'activé(s)' : 'désactivé(s)'}`);
      loadCatalogue();
    }
    if (errors > 0) {
      toast.error(`${errors} erreur(s)`);
    }
  };

  // === ACTIONS GROUPÉES SERVICES ===
  const handleBulkDeleteServices = async () => {
    if (selectedServices.size === 0) {
      toast.error("Aucun service sélectionné");
      return;
    }

    if (!window.confirm(`Supprimer ${selectedServices.size} service(s) ?`)) return;

    let deleted = 0;
    let errors = 0;

    for (const id of selectedServices) {
      try {
        const res = await fetch(`${API_URL}/api/metier/catalogue/services/${id}`, {
          method: "DELETE",
          headers: getAuthHeaders(tenantId),
          credentials: 'include'
        });
        if (res.ok) deleted++;
        else errors++;
      } catch {
        errors++;
      }
    }

    if (deleted > 0) {
      toast.success(`${deleted} service(s) supprimé(s)`);
      loadCatalogue();
    }
    if (errors > 0) {
      toast.error(`${errors} erreur(s) lors de la suppression`);
    }
  };

  const handleBulkToggleServices = async (actif) => {
    if (selectedServices.size === 0) {
      toast.error("Aucun service sélectionné");
      return;
    }

    let updated = 0;
    let errors = 0;

    for (const id of selectedServices) {
      const service = services.find(s => s.id === id);
      if (!service) continue;
      
      try {
        const res = await fetch(`${API_URL}/api/metier/catalogue/services/${id}`, {
          method: "PUT",
          headers: getAuthHeaders(tenantId),
          credentials: 'include',
          body: JSON.stringify({ ...service, actif })
        });
        if (res.ok) updated++;
        else errors++;
      } catch {
        errors++;
      }
    }

    if (updated > 0) {
      toast.success(`${updated} service(s) ${actif ? 'activé(s)' : 'désactivé(s)'}`);
      loadCatalogue();
    }
    if (errors > 0) {
      toast.error(`${errors} erreur(s)`);
    }
  };

  // Ouvrir dialog création/édition produit
  const openProduitDialog = (produit = null) => {
    setEditingItem(produit);
    // Initialiser formData avec toutes les valeurs nécessaires
    const defaultValues = {
      reference: "",
      designation: "",
      description: "",
      description_html: "",
      commentaire: "",
      fiche_cee: "",
      prix_ht: 0,
      taux_tva: 5.5,
      unite: "u",
      type_produit: "equipement",
      categorie: "divers",
      activite: "climat",
      marque: "",
      modele: "",
      puissance_kw: null,
      cop: null,
      scop: null,
      etas: null,
      classe_energie: "",
      actif: true
    };
    // Fusionner avec le produit existant pour préserver tous les champs
    setFormData(produit ? { ...defaultValues, ...produit } : defaultValues);
    setShowProduitDialog(true);
  };

  // Ouvrir dialog création/édition service
  const openServiceDialog = (service = null) => {
    setEditingItem(service);
    // Initialiser formData avec toutes les valeurs nécessaires
    const defaultValues = {
      reference: "",
      designation: "",
      description: "",
      description_html: "",
      fiche_cee: "",
      prix_ht: 0,
      taux_tva: 10,
      unite: "forfait",
      type_service: "main_oeuvre",
      categorie: "installation",
      activite: "climat",
      duree_estimee_heures: null,
      actif: true
    };
    // Fusionner avec le service existant pour préserver tous les champs
    setFormData(service ? { ...defaultValues, ...service } : defaultValues);
    setShowServiceDialog(true);
  };

  // Sauvegarder produit
  const saveProduit = async () => {
    try {
      const url = editingItem?.id 
        ? `${API_URL}/api/metier/catalogue/produits/${editingItem.id}`
        : `${API_URL}/api/metier/catalogue/produits`;
      
      // Nettoyer les valeurs "none" en null et convertir les champs
      const cleanedData = {
        // 🔑 N°890 — OMISE si vide : le backend l'ATTRIBUE par la séquence du
        // tenant. ⛔ Jamais `""` — c'était exactement le déclencheur du 422.
        ...refSaisieOuOmise(formData.reference),
        designation: formData.designation,
        description: formData.description || null,
        description_html: formData.description_html || null,
        commentaire: formData.commentaire || null,
        fiche_cee: formData.fiche_cee === "none" ? null : (formData.fiche_cee || null),
        classe_energie: formData.classe_energie === "none" ? null : (formData.classe_energie || null),
        prix_ht: parseFloat(formData.prix_ht) || 0,
        taux_tva: parseFloat(formData.taux_tva) || 5.5,
        unite: formData.unite || "u",
        // 🔴 MARIO P0 — envoyé SEULEMENT si renseigné (sinon le corps de la requête est identique à celui d'avant, pour tous les tenants).
        ...(formData.base_tarifaire ? { base_tarifaire: formData.base_tarifaire } : {}),
        type_produit: formData.type_produit || "equipement",
        categorie: formData.categorie || "divers",
        activite: formData.activite || "climat",
        marque: formData.marque || null,
        modele: formData.modele || null,
        puissance_kw: formData.puissance_kw ? parseFloat(formData.puissance_kw) : null,
        cop: formData.cop ? parseFloat(formData.cop) : null,
        scop: formData.scop ? parseFloat(formData.scop) : null,
        etas: formData.etas ? parseFloat(formData.etas) : null,
        actif: formData.actif !== false
      };
      
      // ⛔ REFUS AVANT L'ENVOI : inutile d'aller chercher un 422 que l'on sait
      // certain. L'utilisateur voit le champ à remplir, ⛔ pas un échec opaque.
      const manques = champsObligatoiresManquants(cleanedData);
      if (manques.length) {
        toast.error(`À renseigner : ${manques.join(", ")}`);
        return;
      }
      console.log("[Catalogue] Saving produit:", { url, method: editingItem?.id ? "PUT" : "POST", cleanedData });
      
      const response = await fetch(url, {
        method: editingItem?.id ? "PUT" : "POST",
        headers: getAuthHeaders(tenantId),
        credentials: 'include',
        body: JSON.stringify(cleanedData)
      });

      if (response.ok) {
        toast.success(editingItem?.id ? "Produit modifié" : "Produit créé");
        setShowProduitDialog(false);
        loadCatalogue();
      } else {
        const err = await response.json();
        console.error("[Catalogue] Save produit error:", response.status, err);
        toast.error(messageErreurValidation(err.detail, "Erreur lors de l'enregistrement"));
      }
    } catch (error) {
      console.error("[Catalogue] Save produit exception:", error);
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  // Sauvegarder service
  const saveService = async () => {
    try {
      const url = editingItem?.id 
        ? `${API_URL}/api/metier/catalogue/services/${editingItem.id}`
        : `${API_URL}/api/metier/catalogue/services`;
      
      // Nettoyer les données - s'assurer que categorie est valide
      const validCategories = ['installation', 'mise_en_service', 'maintenance', 'déposé', 'raccordement', 'prestations', 'divers'];
      const cleanedData = {
        reference: formData.reference,
        designation: formData.designation,
        description: formData.description || null,
        description_html: formData.description_html || null,
        prix_ht: parseFloat(formData.prix_ht) || 0,
        taux_tva: parseFloat(formData.taux_tva) || 10,
        unite: formData.unite || "forfait",
        categorie: validCategories.includes(formData.categorie) ? formData.categorie : 'divers',
        fiche_cee: formData.fiche_cee === "none" ? null : (formData.fiche_cee || null),
        duree_estimee_heures: formData.duree_estimee_heures ? parseFloat(formData.duree_estimee_heures) : null,
        actif: formData.actif !== false
      };
      
      console.log("[Catalogue] Saving service:", { url, method: editingItem?.id ? "PUT" : "POST", cleanedData });
      
      const response = await fetch(url, {
        method: editingItem?.id ? "PUT" : "POST",
        headers: getAuthHeaders(tenantId),
        credentials: 'include',
        body: JSON.stringify(cleanedData)
      });

      if (response.ok) {
        toast.success(editingItem?.id ? "Service modifié" : "Service créé");
        setShowServiceDialog(false);
        loadCatalogue();
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error("[Catalogue] Save service error:", response.status, errorData);
        toast.error(messageErreurValidation(errorData.detail, "Erreur lors de l'enregistrement"));
      }
    } catch (error) {
      console.error("[Catalogue] Save service exception:", error);
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  // Toggle actif/inactif
  const toggleActif = async (type, item) => {
    try {
      const url = `${API_URL}/api/metier/catalogue/${type}/${item.id}`;
      const response = await fetch(url, {
        method: "PUT",
        headers: getAuthHeaders(tenantId),
        credentials: 'include',
        body: JSON.stringify({ ...item, actif: !item.actif })
      });

      if (response.ok) {
        toast.success(`${item.designation || item.nom} ${!item.actif ? 'activé' : 'désactivé'}`);
        loadCatalogue();
      }
    } catch (error) {
      toast.error("Erreur lors de la modification");
    }
  };

  // Supprimer un élément du catalogue
  const deleteItem = async (type, item) => {
    if (!window.confirm(`Voulez-vous vraiment supprimer "${item.designation || item.nom}" ?`)) {
      return;
    }
    
    try {
      const url = `${API_URL}/api/metier/catalogue/${type}/${item.id}`;
      const response = await fetch(url, {
        method: "DELETE",
        headers: getAuthHeaders(tenantId),
        credentials: 'include'
      });

      if (response.ok) {
        toast.success(`${item.designation || item.nom} supprimé`);
        loadCatalogue();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors de la suppression");
      }
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  // Synchroniser avec Pennylane
  const [syncingPennylane, setSyncingPennylane] = useState(false);
  
  const syncWithPennylane = async () => {
    setSyncingPennylane(true);
    try {
      const response = await fetch(`${API_URL}/api/pennylane/sync-catalog`, {
        method: "POST",
        headers: getAuthHeaders(tenantId),
        credentials: 'include'
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        toast.success(`Synchronisation réussie: ${data.synced || 0} mis à jour, ${data.created || 0} créés`);
        loadCatalogue();
      } else {
        toast.error(data.detail || "Erreur de synchronisation");
      }
    } catch (error) {
      console.error("[Catalogue] Sync Pennylane error:", error);
      toast.error("Erreur lors de la synchronisation avec Pennylane");
    } finally {
      setSyncingPennylane(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--oh-success-text)]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="catalogue-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-medium text-[var(--oh-text)]">Catalogue</h1>
          <p className="text-[var(--oh-text-secondary)]">Gérez vos produits, services et packs</p>
        </div>
        <div className="flex items-center gap-4">
          {/* Sync Pennylane = facturation AMO (TEF). Masqué pour les profils
              installateur (OMV/Climat) qui n'utilisent pas ce flux.
              ET réservé au tenant qui DÉCLARE l'intégration Pennylane
              (feature flag integration.pennylane) — mario_bat n'en a pas. */}
          {!isOmv && hasIntegrationPennylane() && (
            <Button
              data-testid="sync-pennylane-btn"
              variant="outline"
              onClick={syncWithPennylane}
              disabled={syncingPennylane}
              className="border-[var(--oh-info-border)] text-[var(--oh-info-text)] hover:bg-[var(--oh-info-bg)]"
            >
              {syncingPennylane ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Sync Pennylane
            </Button>
          )}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--oh-text-secondary)]" />
            <Input
              data-testid="catalogue-search"
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-64 bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]"
            />
          </div>
          {avecCategorieProduit && (
          <Select value={filterCategorie} onValueChange={setFilterCategorie}>
            <SelectTrigger className="w-full max-w-[180px] bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]" data-testid="catalogue-filtre-categorie">
              <SelectValue placeholder="Catégorie" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes catégories</SelectItem>
              {categories.map(cat => (
                <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          )}
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-lg bg-[var(--oh-accent-bg)]">
              <Package className="h-6 w-6 text-[var(--oh-accent-text)]" />
            </div>
            <div>
              <p className="text-2xl font-medium text-[var(--oh-text)]">{produits.length}</p>
              <p className="text-sm text-[var(--oh-text-secondary)]">Produits</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-lg bg-[var(--oh-accent-bg)]">
              <Wrench className="h-6 w-6 text-[var(--oh-accent-text)]" />
            </div>
            <div>
              <p className="text-2xl font-medium text-[var(--oh-text)]">{services.length}</p>
              <p className="text-sm text-[var(--oh-text-secondary)]">Services</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-[var(--oh-bg-elevated)]">
          <TabsTrigger value="produits" className="data-[state=active]:bg-[var(--oh-accent-bg)]">
            <Package className="h-4 w-4 mr-2" />
            Produits ({filteredProduits.length})
          </TabsTrigger>
          <TabsTrigger value="services" className="data-[state=active]:bg-[var(--oh-accent-bg)]">
            <Wrench className="h-4 w-4 mr-2" />
            Services ({filteredServices.length})
          </TabsTrigger>
          {/* 🔴 MISSIONS AMO — VISIBLE SI LE TENANT DÉCLARE LA CAPABILITÉ.
              ⚠️ Ce test valait `!isOmv` : « tout le monde SAUF OMV ». Mario,
              ni TEF ni OMV, héritait d'un concept AMO/MaPrimeAdapt' qui ⛔ ne
              le concerne PAS — il le voyait à l'écran (mesuré le 17/09).
              🔑 Déclaration POSITIVE, servie par `/user/tenants`, alignée sur
              l'autorité backend (module AMO). ⛔ Pas `!Mario`, ⛔ pas une liste
              d'exclusions : la prochaine arrivée serait encore oubliée. */}
          {peutVoirMissionsAmo && (
            <TabsTrigger value="missions_amo" className="data-[state=active]:bg-[var(--oh-info-bg)]">
              <Euro className="h-4 w-4 mr-2" />
              Missions AMO
            </TabsTrigger>
          )}
          {/* Packs masqués pour l'instant
          <TabsTrigger value="packs" className="data-[state=active]:bg-[var(--oh-success-bg)]">
            <Boxes className="h-4 w-4 mr-2" />
            Packs ({filteredPacks.length})
          </TabsTrigger>
          */}
        </TabsList>

        {/* Produits Tab */}
        <TabsContent value="produits" className="mt-4">
          <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
            <CardHeader className="flex flex-col gap-2 md:flex-row md:gap-0 items-center justify-between">
              <CardTitle className="text-[var(--oh-text)]">Produits</CardTitle>
              <div className="flex items-center gap-3">
                {/* Actions groupées PRODUITS */}
                {selectedProduits.size > 0 && (
                  <div className="flex items-center gap-2 px-4 py-2 bg-[var(--oh-bg-elevated)] rounded-lg border border-[var(--oh-border)]">
                    <span className="text-sm text-[var(--oh-text-muted)]">
                      {selectedProduits.size} sélectionné(s)
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleBulkToggleProduits(true)}
                      className="border-[var(--oh-success-border)] text-[var(--oh-success-text)] hover:bg-[var(--oh-success-bg)]"
                      data-testid="bulk-activate-produits"
                    >
                      <ToggleRight className="w-4 h-4 mr-1" />
                      Activer
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleBulkToggleProduits(false)}
                      className="border-[var(--oh-border)]"
                      data-testid="bulk-deactivate-produits"
                    >
                      <ToggleLeft className="w-4 h-4 mr-1" />
                      Désactiver
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={handleBulkDeleteProduits}
                      data-testid="bulk-delete-produits"
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Supprimer
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setSelectedProduits(new Set()); setSelectAllProduits(false); }}
                    >
                      Annuler
                    </Button>
                  </div>
                )}
                <div className="flex gap-2">
                  <Button 
                    variant="outline"
                    onClick={handleExportCSV}
                    className="border-[var(--oh-border)]"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Exporter CSV
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => setShowImportDialog(true)}
                    className="border-[var(--oh-border)]"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Importer CSV
                  </Button>
                  <Button 
                    data-testid="add-produit-btn"
                    onClick={() => openProduitDialog()}
                    className="bg-[var(--oh-accent-solid)] hover:bg-[var(--oh-accent-solid)]"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Nouveau produit
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="border-[var(--oh-border)]">
                    <TableHead className="w-10">
                      <Checkbox
                        checked={selectAllProduits}
                        onCheckedChange={toggleSelectAllProduits}
                        className="border-[var(--oh-border)]"
                        data-testid="select-all-produits"
                      />
                    </TableHead>
                    <TableHead className="text-[var(--oh-text-secondary)]">Référence</TableHead>
                    <TableHead className="text-[var(--oh-text-secondary)]">Désignation</TableHead>
                    <TableHead className="text-[var(--oh-text-secondary)]">Marque</TableHead>
                    {aRegimeCEE() && (
                      <TableHead className="text-[var(--oh-text-secondary)]">Fiche CEE</TableHead>
                    )}
                    {avecCategorieProduit && (
                      <TableHead className="text-[var(--oh-text-secondary)]">Catégorie</TableHead>
                    )}
                    <TableHead className="text-[var(--oh-text-secondary)] text-right">Prix HT</TableHead>
                    <TableHead className="text-[var(--oh-text-secondary)] text-center">Statut</TableHead>
                    <TableHead className="text-[var(--oh-text-secondary)] text-right">Actions</TableHead>
                  </TableRow>
                  {/* Ligne de filtres */}
                  <TableRow className="border-[var(--oh-border)] bg-[var(--oh-bg-surface)]">
                    <TableHead className="py-2"></TableHead>
                    <TableHead className="py-2">
                      <Input
                        placeholder="Réf..."
                        value={filterNom}
                        onChange={(e) => setFilterNom(e.target.value)}
                        className="h-7 text-xs bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]"
                      />
                    </TableHead>
                    <TableHead className="py-2">
                      <Input
                        placeholder="Nom..."
                        value={filterNom}
                        onChange={(e) => setFilterNom(e.target.value)}
                        className="h-7 text-xs bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]"
                      />
                    </TableHead>
                    <TableHead className="py-2">
                      <Input
                        placeholder="Marque..."
                        value={filterMarque}
                        onChange={(e) => setFilterMarque(e.target.value)}
                        className="h-7 text-xs bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]"
                      />
                    </TableHead>
                    <TableHead className="py-2">
                      <Input
                        placeholder="Fiche..."
                        value={filterFiche}
                        onChange={(e) => setFilterFiche(e.target.value)}
                        className="h-7 text-xs bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]"
                      />
                    </TableHead>
                    {avecCategorieProduit && (
                    <TableHead className="py-2">
                      <Select value={filterCategorieCol} onValueChange={setFilterCategorieCol}>
                        <SelectTrigger className="h-7 text-xs bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
                          <SelectValue placeholder="Catégorie" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Toutes</SelectItem>
                          {categories.map(cat => (
                            <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableHead>
                    )}
                    <TableHead className="py-2"></TableHead>
                    <TableHead className="py-2">
                      <Select value={filterStatutCol} onValueChange={setFilterStatutCol}>
                        <SelectTrigger className="h-7 text-xs bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] w-24">
                          <SelectValue placeholder="Statut" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Tous</SelectItem>
                          <SelectItem value="actif">Actif</SelectItem>
                          <SelectItem value="inactif">Inactif</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableHead>
                    <TableHead className="py-2 text-right">
                      {(filterNom || filterFiche || filterMarque || filterCategorieCol !== "all" || filterStatutCol !== "all") && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setFilterNom("");
                            setFilterFiche("");
                            setFilterMarque("");
                            setFilterCategorieCol("all");
                            setFilterStatutCol("all");
                          }}
                          className="h-7 text-xs text-[var(--oh-text-muted)] hover:text-[var(--oh-text)]"
                        >
                          Effacer
                        </Button>
                      )}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredProduits.map((produit) => {
                    const isSelected = selectedProduits.has(produit.id);
                    return (
                      <TableRow key={produit.id} className={`border-[var(--oh-border)] ${isSelected ? 'bg-[var(--oh-accent-bg)]' : ''}`}>
                        <TableCell className="w-10">
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleSelectProduit(produit.id)}
                            className="border-[var(--oh-border)]"
                            data-testid={`select-produit-${produit.id}`}
                          />
                        </TableCell>
                        <TableCell className="text-[var(--oh-info-text)] font-mono text-xs">
                          {produit.reference || "-"}
                        </TableCell>
                        <TableCell className="text-[var(--oh-text)] font-medium text-sm">
                          {produit.designation}
                          {produit.commentaire && (
                            <div
                              data-testid={`produit-commentaire-${produit.id}`}
                              className="text-xs font-normal text-[var(--oh-text-secondary)] mt-1 max-w-[420px] truncate"
                              title={produit.commentaire}
                            >
                              {produit.commentaire}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-[var(--oh-text-secondary)] text-sm">{produit.marque || "-"}</TableCell>
                        {aRegimeCEE() && (
                        <TableCell>
                          {produit.fiche_cee ? (
                            <Badge className="bg-[var(--oh-success-bg)] text-[var(--oh-success-text)] text-xs font-mono">
                              {produit.fiche_cee}
                            </Badge>
                          ) : (
                            <span className="text-[var(--oh-text-secondary)]">-</span>
                          )}
                        </TableCell>
                        )}
                        {avecCategorieProduit && (
                        <TableCell className="text-[var(--oh-text-secondary)] text-sm">
                          {CATEGORIES_PRODUIT.find(c => c.value === produit.categorie)?.label || produit.categorie || "-"}
                        </TableCell>
                        )}
                        <TableCell className="text-right text-[var(--oh-success-text)]">{formatPrice(produit.prix_ht)}</TableCell>
                        <TableCell className="text-center">
                          <Badge variant={produit.actif ? "success" : "secondary"}>
                            {produit.actif ? "Actif" : "Inactif"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => toggleActif("produits", produit)}
                              title={produit.actif ? "Désactiver" : "Activer"}
                            >
                              {produit.actif ? <ToggleRight className="h-4 w-4 text-[var(--oh-success-text)]" /> : <ToggleLeft className="h-4 w-4 text-[var(--oh-text-secondary)]" />}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openProduitDialog(produit)}
                              title="Modifier"
                            >
                              <Edit className="h-4 w-4 text-[var(--oh-accent-text)]" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteItem("produits", produit)}
                              title="Supprimer"
                            >
                              <Trash2 className="h-4 w-4 text-[var(--oh-error-text)]" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Services Tab */}
        <TabsContent value="services" className="mt-4">
          <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
            <CardHeader className="flex flex-col gap-2 md:flex-row md:gap-0 items-center justify-between">
              <CardTitle className="text-[var(--oh-text)]">Services (Main d'œuvre)</CardTitle>
              <div className="flex items-center gap-3">
                {/* Actions groupées SERVICES */}
                {selectedServices.size > 0 && (
                  <div className="flex items-center gap-2 px-4 py-2 bg-[var(--oh-bg-elevated)] rounded-lg border border-[var(--oh-border)]">
                    <span className="text-sm text-[var(--oh-text-muted)]">
                      {selectedServices.size} sélectionné(s)
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleBulkToggleServices(true)}
                      className="border-[var(--oh-success-border)] text-[var(--oh-success-text)] hover:bg-[var(--oh-success-bg)]"
                      data-testid="bulk-activate-services"
                    >
                      <ToggleRight className="w-4 h-4 mr-1" />
                      Activer
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleBulkToggleServices(false)}
                      className="border-[var(--oh-border)]"
                      data-testid="bulk-deactivate-services"
                    >
                      <ToggleLeft className="w-4 h-4 mr-1" />
                      Désactiver
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={handleBulkDeleteServices}
                      data-testid="bulk-delete-services"
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Supprimer
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setSelectedServices(new Set()); setSelectAllServices(false); }}
                    >
                      Annuler
                    </Button>
                  </div>
                )}
                <div className="flex gap-2">
                  <Button 
                    variant="outline"
                    onClick={handleExportCSV}
                    className="border-[var(--oh-border)]"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Exporter CSV
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => setShowImportDialog(true)}
                    className="border-[var(--oh-border)]"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Importer CSV
                  </Button>
                  <Button 
                    data-testid="add-service-btn"
                    onClick={() => openServiceDialog()}
                    className="bg-[var(--oh-accent-solid)] hover:bg-[var(--oh-accent-solid)]"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Nouveau service
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="border-[var(--oh-border)]">
                    <TableHead className="w-10">
                      <Checkbox
                        checked={selectAllServices}
                        onCheckedChange={toggleSelectAllServices}
                        className="border-[var(--oh-border)]"
                        data-testid="select-all-services"
                      />
                    </TableHead>
                    <TableHead className="text-[var(--oh-text-secondary)]">Référence</TableHead>
                    <TableHead className="text-[var(--oh-text-secondary)]">Désignation</TableHead>
                    <TableHead className="text-[var(--oh-text-secondary)]">Catégorie</TableHead>
                    <TableHead className="text-[var(--oh-text-secondary)] text-right">Prix HT</TableHead>
                    <TableHead className="text-[var(--oh-text-secondary)] text-center">Unité</TableHead>
                    <TableHead className="text-[var(--oh-text-secondary)] text-center">Statut</TableHead>
                    <TableHead className="text-[var(--oh-text-secondary)] text-right">Actions</TableHead>
                  </TableRow>
                  {/* Ligne de filtres Services */}
                  <TableRow className="border-[var(--oh-border)] bg-[var(--oh-bg-surface)]">
                    <TableHead className="py-2"></TableHead>
                    <TableHead className="py-2">
                      <Input
                        placeholder="Réf..."
                        value={filterServiceNom}
                        onChange={(e) => setFilterServiceNom(e.target.value)}
                        className="h-7 text-xs bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]"
                      />
                    </TableHead>
                    <TableHead className="py-2">
                      <Input
                        placeholder="Nom..."
                        value={filterServiceNom}
                        onChange={(e) => setFilterServiceNom(e.target.value)}
                        className="h-7 text-xs bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]"
                      />
                    </TableHead>
                    <TableHead className="py-2">
                      <Select value={filterServiceCategorieCol} onValueChange={setFilterServiceCategorieCol}>
                        <SelectTrigger className="h-7 text-xs bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
                          <SelectValue placeholder="Catégorie" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Toutes</SelectItem>
                          {CATEGORIES_SERVICE.map(cat => (
                            <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableHead>
                    <TableHead className="py-2"></TableHead>
                    <TableHead className="py-2"></TableHead>
                    <TableHead className="py-2">
                      <Select value={filterServiceStatutCol} onValueChange={setFilterServiceStatutCol}>
                        <SelectTrigger className="h-7 text-xs bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] w-24">
                          <SelectValue placeholder="Statut" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Tous</SelectItem>
                          <SelectItem value="actif">Actif</SelectItem>
                          <SelectItem value="inactif">Inactif</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableHead>
                    <TableHead className="py-2 text-right">
                      {(filterServiceNom || filterServiceCategorieCol !== "all" || filterServiceStatutCol !== "all") && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setFilterServiceNom("");
                            setFilterServiceCategorieCol("all");
                            setFilterServiceStatutCol("all");
                          }}
                          className="h-7 text-xs text-[var(--oh-text-muted)] hover:text-[var(--oh-text)]"
                        >
                          Effacer
                        </Button>
                      )}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredServices.map((service) => {
                    const isSelected = selectedServices.has(service.id);
                    return (
                      <TableRow key={service.id} className={`border-[var(--oh-border)] ${isSelected ? 'bg-[var(--oh-accent-bg)]' : ''}`}>
                        <TableCell className="w-10">
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleSelectService(service.id)}
                            className="border-[var(--oh-border)]"
                            data-testid={`select-service-${service.id}`}
                          />
                        </TableCell>
                        <TableCell className="text-[var(--oh-accent-text)] font-mono text-xs">
                          {service.reference || "-"}
                        </TableCell>
                        <TableCell className="text-[var(--oh-text)] font-medium text-sm">{service.designation || service.nom}</TableCell>
                        <TableCell className="text-[var(--oh-text-secondary)] text-sm">
                          {CATEGORIES_SERVICE.find(c => c.value === service.categorie)?.label || service.categorie || "-"}
                        </TableCell>
                        <TableCell className="text-right text-[var(--oh-accent-text)]">{formatPrice(service.prix_ht)}</TableCell>
                        <TableCell className="text-center text-[var(--oh-text-secondary)] text-sm">{service.unite || "forfait"}</TableCell>
                        <TableCell className="text-center">
                          <Badge variant={service.actif ? "success" : "secondary"}>
                            {service.actif ? "Actif" : "Inactif"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => toggleActif("services", service)}
                              title={service.actif ? "Désactiver" : "Activer"}
                            >
                              {service.actif ? <ToggleRight className="h-4 w-4 text-[var(--oh-success-text)]" /> : <ToggleLeft className="h-4 w-4 text-[var(--oh-text-secondary)]" />}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openServiceDialog(service)}
                              title="Modifier"
                            >
                              <Edit className="h-4 w-4 text-[var(--oh-accent-text)]" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteItem("services", service)}
                              title="Supprimer"
                            >
                              <Trash2 className="h-4 w-4 text-[var(--oh-error-text)]" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Packs Tab */}
        {/* Missions AMO Tab */}
        <TabsContent value="missions_amo" className="mt-4">
          <MissionsAMOTab apiUrl={API_URL} tenantId={tenantId} getAuthHeaders={getAuthHeaders} />
        </TabsContent>

        <TabsContent value="packs" className="mt-4">
          <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
            <CardHeader className="flex flex-col gap-2 md:flex-row md:gap-0 items-center justify-between">
              <CardTitle className="text-[var(--oh-text)]">Packs / Templates de devis</CardTitle>
              <Button 
                data-testid="add-pack-btn"
                disabled
                className="bg-green-600 hover:bg-green-700 opacity-50"
              >
                <Plus className="h-4 w-4 mr-2" />
                Nouveau pack (bientôt)
              </Button>
            </CardHeader>
            <CardContent>
              {filteredPacks.length === 0 ? (
                <div className="text-center py-8 text-[var(--oh-text-secondary)]">
                  <Boxes className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Aucun pack configuré</p>
                  <p className="text-sm mt-2">Les packs permettent de créer des devis pré-configurés</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-[var(--oh-border)]">
                      <TableHead className="text-[var(--oh-text-secondary)]">Nom</TableHead>
                      <TableHead className="text-[var(--oh-text-secondary)]">Produits</TableHead>
                      <TableHead className="text-[var(--oh-text-secondary)]">Services</TableHead>
                      <TableHead className="text-[var(--oh-text-secondary)] text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredPacks.map((pack) => (
                      <TableRow key={pack.id} className="border-[var(--oh-border)]">
                        <TableCell className="text-[var(--oh-text)] font-medium">{pack.nom}</TableCell>
                        <TableCell className="text-[var(--oh-text-secondary)]">{pack.produits?.length || 0}</TableCell>
                        <TableCell className="text-[var(--oh-text-secondary)]">{pack.services?.length || 0}</TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" disabled>
                            <Edit className="h-4 w-4 text-[var(--oh-text-secondary)]" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog Produit */}
      <Dialog open={showProduitDialog} onOpenChange={setShowProduitDialog}>
        <DialogContent className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-[var(--oh-text)]">
              {editingItem?.id ? "Modifier le produit" : "Nouveau produit"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[70vh] overflow-y-auto">
            {/* Section: Identification */}
            <div className="border-b border-[var(--oh-border)] pb-4">
              <h4 className="text-sm font-medium text-[var(--oh-accent-text)] mb-3">Identification</h4>
              
              {/* Référence fabricant */}
              <div className="space-y-2 mb-3">
                <label className="text-sm text-[var(--oh-info-text)] font-medium">Référence fabricant *</label>
                <Input
                  data-testid="produit-reference-input"
                  value={formData.reference || ""}
                  onChange={(e) => setFormData({...formData, reference: e.target.value.toUpperCase()})}
                  className="bg-[var(--oh-bg-elevated)] border-[var(--oh-info-border)] focus:border-[var(--oh-info-border)]"
                  placeholder="ALTHERMA-3-11KW, CALYPSO-VS-270, etc."
                />
                <p className="text-xs text-[var(--oh-text-secondary)]">Référence unique du produit chez le fabricant</p>
              </div>

              {/* Marque et Modèle */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-3">
                <div className="space-y-2">
                  <label className="text-sm text-[var(--oh-text-secondary)]">Marque</label>
                  <Input
                    value={formData.marque || ""}
                    onChange={(e) => setFormData({...formData, marque: e.target.value})}
                    className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]"
                    placeholder="Atlantic, Daikin, Mitsubishi..."
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm text-[var(--oh-text-secondary)]">Modèle</label>
                  <Input
                    value={formData.modele || ""}
                    onChange={(e) => setFormData({...formData, modele: e.target.value})}
                    className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]"
                    placeholder="Altherma 3, Calypso VS..."
                  />
                </div>
              </div>

              {/* Désignation */}
              <div className="space-y-2">
                <label className="text-sm text-[var(--oh-text-secondary)]">Désignation *</label>
                <Input
                  data-testid="produit-designation-input"
                  value={formData.designation || ""}
                  onChange={(e) => setFormData({...formData, designation: e.target.value})}
                  className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]"
                  placeholder="PAC Air/Eau 11kW Haute température"
                />
              </div>
            </div>
            
            {/* Section: Fiche CEE — 🔴 N°728-4 : la liste de slugs
                (`climatpac` + `omv_france`, CC-DEV1-27) est REMPLACÉE par le
                régime d'aides DÉCLARÉ. Un tenant CEE de plus ⛔ ne demandera
                plus de toucher ce fichier. */}
            {aRegimeCEE() && (
            <div className="border-b border-[var(--oh-border)] pb-4">
              <h4 className="text-sm font-medium text-[var(--oh-success-text)] mb-3">Fiche Opération CEE</h4>
              <div className="space-y-2">
                <label className="text-sm text-[var(--oh-text-secondary)]">Code fiche CEE</label>
                <Select
                  value={formData.fiche_cee || "none"}
                  onValueChange={(value) => setFormData({...formData, fiche_cee: value === "none" ? null : value})}
                >
                  <SelectTrigger className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
                    <SelectValue placeholder="Sélectionner une fiche CEE" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucune</SelectItem>
                    {fichesCEE.map(f => (
                      <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-[var(--oh-text-secondary)]">Fiche opération standardisée (ex: BAR-TH-148 pour ballon thermodynamique)</p>
              </div>
            </div>
            )}

            {/* Section: Classification */}
            {(avecTypeProduit || avecCategorieProduit) && (
            <div className="border-b border-[var(--oh-border)] pb-4" data-testid="produit-section-classification">
              <h4 className="text-sm font-medium text-[var(--oh-accent-text)] mb-3">Classification</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {avecTypeProduit && (
                <div className="space-y-2">
                  <label className="text-sm text-[var(--oh-text-secondary)]">Type *</label>
                  <Select
                    value={formData.type_produit || "equipement"}
                    onValueChange={(value) => setFormData({...formData, type_produit: value})}
                  >
                    <SelectTrigger className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TYPES_PRODUIT.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                )}
                {avecCategorieProduit && (
                <div className="space-y-2">
                  <label className="text-sm text-[var(--oh-text-secondary)]">Catégorie *</label>
                  <Select
                    value={formData.categorie || "divers"}
                    onValueChange={(value) => setFormData({...formData, categorie: value})}
                  >
                    <SelectTrigger className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]" data-testid="produit-categorie-trigger">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES_PRODUIT.map(c => (
                        <SelectItem key={c.value} value={c.value} data-testid={`produit-categorie-opt-${c.value}`}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                )}
              </div>
            </div>
            )}

            {/* Section: Prix */}
            <div className="border-b border-[var(--oh-border)] pb-4">
              <h4 className="text-sm font-medium text-cyan-400 mb-3">Tarification</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <label className="text-sm text-[var(--oh-text-secondary)]">Prix HT *</label>
                  <div className="relative">
                    <Euro className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--oh-text-secondary)]" />
                    <Input
                      data-testid="produit-prix-input"
                      type="number"
                      step="0.01"
                      value={formData.prix_ht || 0}
                      onChange={(e) => setFormData({...formData, prix_ht: parseFloat(e.target.value)})}
                      className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] pl-10"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm text-[var(--oh-text-secondary)]">TVA</label>
                  <Select
                    value={String(formData.taux_tva || 5.5)}
                    onValueChange={(value) => setFormData({...formData, taux_tva: parseFloat(value)})}
                  >
                    <SelectTrigger className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">0%</SelectItem>
                      <SelectItem value="5.5">5.5%</SelectItem>
                      <SelectItem value="10">10%</SelectItem>
                      <SelectItem value="20">20%</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm text-[var(--oh-text-secondary)]">Unité</label>
                  <Select
                    value={formData.unite || "u"}
                    onValueChange={(value) => setFormData({...formData, unite: value})}
                  >
                    <SelectTrigger className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="u">Unité</SelectItem>
                      <SelectItem value="m²">m²</SelectItem>
                      <SelectItem value="ml">ml</SelectItem>
                      <SelectItem value="kit">Kit</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {/* 🔴 MARIO P0 — nature du prix (visible seulement : profil chantier ET unité de mesure) ; sinon rien. */}
                <BaseTarificationField unite={formData.unite} value={formData.base_tarifaire}
                                       onChange={(v) => setFormData({ ...formData, base_tarifaire: v })} />
              </div>
            </div>
            
            {/* Section: Caractéristiques techniques — CONDITIONNELLES au type/catégorie (CC-DEV1-27).
                VMC → débit d'air / nb caissons / puissance ; isolation → résistance R ; PAC (et
                défaut) → puissance / COP / SCOP / classe. Une VMC n'affiche PLUS de COP/SCOP. */}
            {(() => {
              const _catLabel = (CATEGORIES_PRODUIT.find(c => c.value === formData.categorie)?.label) || "";
              const _catStr = `${formData.categorie || ""} ${_catLabel}`.toLowerCase();
              const isVmc = /vmc|ventil/.test(_catStr);
              const isIsolation = /isol/.test(_catStr);
              // 🔑 Le cas THERMIQUE est désormais NOMMÉ, ⛔ il n'est plus « tout
              // le reste » : PAC, chauffage, climatisation, ballon, chauffe-eau.
              const isThermique = /pac|pompe|chauff|clim|thermo|ballon|eau chaude/.test(_catStr);
              const numOrNull = (v) => (v === "" || v == null ? null : parseFloat(v));
              const intOrNull = (v) => (v === "" || v == null ? null : parseInt(v, 10));
              return (
                <div>
                  <h4 className="text-sm font-medium text-[var(--oh-info-text)] mb-3">Caractéristiques techniques</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
                    {isVmc ? (<>
                      <div className="space-y-2">
                        <label className="text-sm text-[var(--oh-text-secondary)]">Débit d'air (m³/h)</label>
                        <Input type="number" step="1" data-testid="produit-debit-air"
                          value={formData.debit_air_m3h || ""}
                          onChange={(e) => setFormData({...formData, debit_air_m3h: numOrNull(e.target.value)})}
                          className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]" placeholder="1500" />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm text-[var(--oh-text-secondary)]">Nb caissons</label>
                        <Input type="number" step="1" data-testid="produit-nb-caissons"
                          value={formData.nb_caissons || ""}
                          onChange={(e) => setFormData({...formData, nb_caissons: intOrNull(e.target.value)})}
                          className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]" placeholder="1" />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm text-[var(--oh-text-secondary)]">Puissance (kW)</label>
                        <Input type="number" step="0.1"
                          value={formData.puissance_kw || ""}
                          onChange={(e) => setFormData({...formData, puissance_kw: numOrNull(e.target.value)})}
                          className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]" placeholder="0.5" />
                      </div>
                    </>) : isIsolation ? (
                      <div className="space-y-2">
                        <label className="text-sm text-[var(--oh-text-secondary)]">Résistance thermique R (m².K/W)</label>
                        <Input type="number" step="0.05" data-testid="produit-resistance-r"
                          value={formData.resistance_thermique_r || ""}
                          onChange={(e) => setFormData({...formData, resistance_thermique_r: numOrNull(e.target.value)})}
                          className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]" placeholder="7.0" />
                      </div>
                    ) : isThermique ? (<>
                      <div className="space-y-2">
                        <label className="text-sm text-[var(--oh-text-secondary)]">Puissance (kW)</label>
                        <Input type="number" step="0.1"
                          value={formData.puissance_kw || ""}
                          onChange={(e) => setFormData({...formData, puissance_kw: numOrNull(e.target.value)})}
                          className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]" placeholder="11" />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm text-[var(--oh-text-secondary)]">COP</label>
                        <Input type="number" step="0.01" data-testid="produit-cop"
                          value={formData.cop || ""}
                          onChange={(e) => setFormData({...formData, cop: numOrNull(e.target.value)})}
                          className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]" placeholder="4.5" />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm text-[var(--oh-text-secondary)]">SCOP</label>
                        <Input type="number" step="0.01" data-testid="produit-scop"
                          value={formData.scop || ""}
                          onChange={(e) => setFormData({...formData, scop: numOrNull(e.target.value)})}
                          className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]" placeholder="4.2" />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm text-[var(--oh-text-secondary)]">Classe énergie</label>
                        <Select value={formData.classe_energie || ""}
                          onValueChange={(value) => setFormData({...formData, classe_energie: value})}>
                          <SelectTrigger className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
                            <SelectValue placeholder="-" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">-</SelectItem>
                            <SelectItem value="A+++">A+++</SelectItem>
                            <SelectItem value="A++">A++</SelectItem>
                            <SelectItem value="A+">A+</SelectItem>
                            <SelectItem value="A">A</SelectItem>
                            <SelectItem value="B">B</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </>) : (
                      /* 🔴🔴 N°705-5 — LE DÉFAUT EST « AUCUNE CARACTÉRISTIQUE ».
                         ⚠️ MESURÉ : la branche par défaut était CELLE DE LA PAC
                         ⇒ un produit de PEINTURE affichait Puissance / COP /
                         SCOP / classe énergie. ⛔ Un champ d'un autre métier
                         posé sous les yeux, et pire : offert à la SAISIE.
                         ✅ La base est saine (0 champ Climat sur les 21
                         produits de Mario) — c'était l'écran qui inventait.
                         🔑 Le DÉFAUT d'un conditionnel doit être le cas NEUTRE,
                         ⛔ jamais le cas d'un métier particulier. */
                      <div className="text-sm col-span-full"
                           data-testid="catalogue-carac-aucune"
                           style={{ color: "var(--oh-text-secondary)" }}>
                        Aucune caractéristique technique pour cette catégorie.
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
            
            {/* Section: Commentaire libre du produit */}
            <div className="space-y-2">
              <label htmlFor="produit-commentaire-input" className="text-sm text-[var(--oh-text-secondary)]">Commentaire</label>
              <Textarea
                id="produit-commentaire-input"
                data-testid="produit-commentaire-input"
                aria-label="Commentaire du produit"
                maxLength={1000}
                rows={3}
                value={formData.commentaire || ""}
                onChange={(e) => setFormData({...formData, commentaire: e.target.value})}
                className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]"
                placeholder="Précision libre sur ce produit (visible à la réouverture de la fiche)"
              />
            </div>

            {/* Section: Informations complémentaires (Éditeur intelligent) */}
            <div>
              <h4 className="text-sm font-medium text-pink-400 mb-3">Informations complémentaires</h4>
              <p className="text-xs text-[var(--oh-text-secondary)] mb-2">
                Ajoutez des informations détaillées : caractéristiques, conditions, garanties, etc.
              </p>
              <SmartLegalEditor
                value={formData.description_html || ""}
                onChange={(html) => setFormData({...formData, description_html: html})}
                placeholder="Collez ou saisissez des informations complémentaires sur ce produit..."
                minHeight="120px"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProduitDialog(false)}>
              Annuler
            </Button>
            <Button 
              data-testid="save-produit-btn"
              onClick={saveProduit} 
              className="bg-[var(--oh-accent-solid)] hover:bg-[var(--oh-accent-solid)]"
            >
              {editingItem?.id ? "Enregistrer" : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Service */}
      <Dialog open={showServiceDialog} onOpenChange={setShowServiceDialog}>
        <DialogContent className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] max-w-xl">
          <DialogHeader>
            <DialogTitle className="text-[var(--oh-text)]">
              {editingItem?.id ? "Modifier le service" : "Nouveau service"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[70vh] overflow-y-auto">
            {/* Section: Identification */}
            <div className="border-b border-[var(--oh-border)] pb-4">
              <h4 className="text-sm font-medium text-[var(--oh-accent-text)] mb-3">Identification</h4>
              <div className="space-y-3">
                <div className="space-y-2">
                  <label className="text-sm text-[var(--oh-info-text)] font-medium">Référence *</label>
                  <Input
                    data-testid="service-reference-input"
                    value={formData.reference || ""}
                    onChange={(e) => setFormData({...formData, reference: e.target.value.toUpperCase()})}
                    className="bg-[var(--oh-bg-elevated)] border-[var(--oh-info-border)] focus:border-[var(--oh-info-border)]"
                    placeholder="MO-INST-PAC, MO-DEPOSE, etc."
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm text-[var(--oh-text-secondary)]">Désignation *</label>
                  <Input
                    data-testid="service-designation-input"
                    value={formData.designation || ""}
                    onChange={(e) => setFormData({...formData, designation: e.target.value})}
                    className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]"
                    placeholder="Installation PAC Air/Eau complète"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm text-[var(--oh-text-secondary)]">Description</label>
                  <Input
                    value={formData.description || ""}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]"
                    placeholder="Inclut mise en service, raccordements..."
                  />
                </div>
              </div>
            </div>
            
            {/* Section: Classification */}
            <div className="border-b border-[var(--oh-border)] pb-4">
              <h4 className="text-sm font-medium text-[var(--oh-accent-text)] mb-3">Classification</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm text-[var(--oh-text-secondary)]">Type</label>
                  <Select
                    value={formData.type_service || "main_oeuvre"}
                    onValueChange={(value) => setFormData({...formData, type_service: value})}
                  >
                    <SelectTrigger className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TYPES_SERVICE.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm text-[var(--oh-text-secondary)]">Catégorie</label>
                  <Select
                    value={formData.categorie || "installation"}
                    onValueChange={(value) => setFormData({...formData, categorie: value})}
                  >
                    <SelectTrigger className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES_SERVICE.map(c => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Fiche CEE associée — 🔴 N°728-4 : ⛔ plus `tenantId === "climatpac"`.
                  ⚠️ CHANGEMENT ASSUMÉ : ce bloc s'ouvre maintenant à TOUT tenant
                  de régime CEE (donc aussi omv_france). C'est la MÊME donnée
                  (`fiche_cee`) que le formulaire Produit lui expose déjà juste
                  au-dessus : la restriction au seul slug climatpac était une
                  incohérence, ⛔ pas une règle métier. */}
              {aRegimeCEE() && (
                <div className="space-y-2 mt-3">
                  <label className="text-sm text-[var(--oh-text-secondary)]">Fiche CEE associée (optionnel)</label>
                  <Select
                    value={formData.fiche_cee || "none"}
                    onValueChange={(value) => setFormData({...formData, fiche_cee: value === "none" ? null : value})}
                  >
                    <SelectTrigger className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
                      <SelectValue placeholder="Aucune" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucune</SelectItem>
                      {fichesCEE.map(f => (
                        <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Section: Tarification */}
            <div>
              <h4 className="text-sm font-medium text-[var(--oh-success-text)] mb-3">Tarification</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <label className="text-sm text-[var(--oh-text-secondary)]">Prix HT *</label>
                  <div className="relative">
                    <Euro className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--oh-text-secondary)]" />
                    <Input
                      data-testid="service-prix-input"
                      type="number"
                      step="0.01"
                      value={formData.prix_ht || 0}
                      onChange={(e) => setFormData({...formData, prix_ht: parseFloat(e.target.value)})}
                      className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] pl-10"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm text-[var(--oh-text-secondary)]">TVA</label>
                  <Select
                    value={String(formData.taux_tva || 10)}
                    onValueChange={(value) => setFormData({...formData, taux_tva: parseFloat(value)})}
                  >
                    <SelectTrigger className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">0%</SelectItem>
                      <SelectItem value="5.5">5.5%</SelectItem>
                      <SelectItem value="10">10%</SelectItem>
                      <SelectItem value="20">20%</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm text-[var(--oh-text-secondary)]">Unité</label>
                  <Select
                    value={formData.unite || "forfait"}
                    onValueChange={(value) => setFormData({...formData, unite: value})}
                  >
                    <SelectTrigger className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="forfait">Forfait</SelectItem>
                      <SelectItem value="h">Heure</SelectItem>
                      <SelectItem value="jour">Jour</SelectItem>
                      <SelectItem value="u">Unité</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2 mt-3">
                <label className="text-sm text-[var(--oh-text-secondary)]">Durée estimée (heures)</label>
                <Input
                  type="number"
                  step="0.5"
                  value={formData.duree_estimee_heures || ""}
                  onChange={(e) => setFormData({...formData, duree_estimee_heures: e.target.value ? parseFloat(e.target.value) : null})}
                  className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] w-32"
                  placeholder="4"
                />
              </div>
            </div>
            
            {/* Section: Informations complémentaires (Éditeur intelligent) */}
            <div>
              <h4 className="text-sm font-medium text-pink-400 mb-3">Informations complémentaires</h4>
              <p className="text-xs text-[var(--oh-text-secondary)] mb-2">
                Détails du service : inclusions, exclusions, conditions, etc.
              </p>
              <SmartLegalEditor
                value={formData.description_html || ""}
                onChange={(html) => setFormData({...formData, description_html: html})}
                placeholder="Collez ou saisissez des informations complémentaires sur ce service..."
                minHeight="120px"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowServiceDialog(false)}>
              Annuler
            </Button>
            <Button 
              data-testid="save-service-btn"
              onClick={saveService}
              className="bg-[var(--oh-accent-solid)] hover:bg-[var(--oh-accent-solid)]"
              disabled={!formData.reference || !formData.designation}
            >
              {editingItem?.id ? "Enregistrer" : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Import CSV */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[var(--oh-text)] flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-[var(--oh-success-text)]" />
              Importer depuis CSV
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-4 bg-[var(--oh-bg-elevated)] rounded-lg border border-dashed border-[var(--oh-border)]">
              <input
                type="file"
                accept=".csv"
                onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                className="w-full text-sm text-[var(--oh-text-muted)] file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-medium file:bg-[var(--oh-accent-solid)] file:text-[var(--oh-accent-contrast)] hover:file:bg-[var(--oh-accent-solid)]"
              />
              {importFile && (
                <p className="mt-2 text-sm text-[var(--oh-success-text)]">
                  ✓ {importFile.name}
                </p>
              )}
            </div>
            
            <div className="text-xs text-[var(--oh-text-muted)] space-y-1">
              <p><strong>Format attendu :</strong> CSV avec séparateur point-virgule (;)</p>
              <p><strong>Colonnes {activeTab === "produits" ? "produits" : "services"} :</strong></p>
              <p className="font-mono text-[var(--oh-text-secondary)]">
                {activeTab === "produits" 
                  ? "reference;nom;categorie;marque;modele;prix_ht;taux_tva;fiche_cee;actif;description"
                  : "reference;nom;categorie;prix_ht;taux_tva;unite;actif;description"
                }
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowImportDialog(false);
                setImportFile(null);
              }}
            >
              Annuler
            </Button>
            <Button
              onClick={handleImportCSV}
              disabled={!importFile || importing}
              className="bg-green-600 hover:bg-green-700"
            >
              {importing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Import...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Importer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
